import React, { useState } from 'react';
import { Button, ProgressBar, Alert, Modal, Form } from 'react-bootstrap';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface MaintenanceReceiptUploaderProps {
  bikeId: string;
  existingUrl?: string;
  onUploadComplete: (url: string) => void;
  onUploadError?: (error: string) => void;
  onDelete?: () => void;
  disabled?: boolean;
}

const MaintenanceReceiptUploader: React.FC<MaintenanceReceiptUploaderProps> = ({ 
  bikeId, 
  existingUrl, 
  onUploadComplete, 
  onUploadError,
  onDelete,
  disabled = false
}) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { currentUser } = useAuth();

  const allowedFileTypes = ['application/pdf'];

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check if file type is allowed
    if (!allowedFileTypes.includes(file.type)) {
      setError('Nur PDF-Dateien sind erlaubt.');
      return;
    }

    // Reset states
    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    // Create storage reference with the specified path structure
    const fileName = `${Date.now()}_${file.name}`;
    const fileStorageRef = storageRef(
      storage, 
      `bikes/${currentUser?.uid}/${bikeId}/bikeMaintenanceReceipts/${fileName}`
    );

    // Start upload
    const uploadTask = uploadBytesResumable(fileStorageRef, file);

    // Listen for state changes
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        console.error('Upload error:', error);
        setError('Fehler beim Hochladen. Bitte versuche es erneut.');
        setIsUploading(false);
        if (onUploadError) onUploadError('Fehler beim Hochladen');
      },
      async () => {
        // Upload completed successfully
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          onUploadComplete(downloadUrl);
          setIsUploading(false);
        } catch (error) {
          console.error('Error getting download URL:', error);
          setError('Fehler beim Abrufen der Datei-URL');
          setIsUploading(false);
          if (onUploadError) onUploadError('Fehler beim Abrufen der Datei-URL');
        }
      }
    );
  };

  const handleDelete = async () => {
    if (!existingUrl || !onDelete) return;
    
    try {
      // Extract the path from the URL
      const fileUrlPath = existingUrl.split('?')[0].split('/o/')[1];
      if (!fileUrlPath) throw new Error('Invalid file URL');
      
      const decodedPath = decodeURIComponent(fileUrlPath);
      const fileRef = storageRef(storage, decodedPath);
      
      await deleteObject(fileRef);
      onDelete();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Fehler beim Löschen der Datei');
    }
  };

  const handleOpenModal = () => {
    if (existingUrl) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>Beleg (PDF)</Form.Label>
        
        {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
        
        {existingUrl && (
          <div className="mb-3">
            <div className="d-flex align-items-center">
              <Button 
                variant="link" 
                className="p-0 d-flex align-items-center" 
                onClick={handleOpenModal}
                disabled={disabled}
              >
                <i className="bi bi-file-earmark-pdf me-2 fs-4 text-danger"></i>
                <span>Beleg anzeigen</span>
              </Button>
            </div>
          </div>
        )}
        
        {/* Modal for viewing PDF */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          size="lg" 
          centered
          className="file-viewer-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Wartungsbeleg</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {existingUrl && (
              <div className="pdf-container">
                <div className="ratio ratio-16x9" style={{ minHeight: '500px' }}>
                  <iframe 
                    src={`${existingUrl}#toolbar=1&navpanes=1&scrollbar=1`} 
                    title="PDF Viewer" 
                    width="100%" 
                    height="100%" 
                    frameBorder="0"
                    allowFullScreen
                  >
                    <p>Ihr Browser unterstützt keine eingebetteten PDFs. <a href={existingUrl} target="_blank" rel="noopener noreferrer">PDF herunterladen</a></p>
                  </iframe>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Schließen
            </Button>
            <Button 
              variant="primary" 
              href={existingUrl} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              In neuem Tab öffnen
            </Button>
          </Modal.Footer>
        </Modal>
        
        {isUploading && (
          <div className="my-3">
            <ProgressBar 
              now={uploadProgress} 
              label={`${uploadProgress}%`} 
              variant="info" 
            />
          </div>
        )}
        
        <div className="d-flex flex-wrap gap-2 mt-2">
          <Button
            as="label"
            htmlFor={`receipt-upload-${bikeId}`}
            variant={existingUrl ? "outline-primary" : "primary"}
            disabled={isUploading || disabled}
            size="sm"
          >
            <i className="bi bi-upload me-2"></i>
            {existingUrl ? 'Beleg ändern' : 'Beleg hochladen'}
            <input
              id={`receipt-upload-${bikeId}`}
              type="file"
              onChange={handleFileSelection}
              accept=".pdf"
              style={{ display: 'none' }}
              disabled={isUploading || disabled}
            />
          </Button>
          
          {existingUrl && (
            <Button 
              variant="outline-danger" 
              onClick={handleDelete}
              disabled={isUploading || disabled}
              size="sm"
            >
              <i className="bi bi-trash me-2"></i>
              Beleg löschen
            </Button>
          )}
        </div>
        
        <Form.Text className="text-muted">
          Nur PDF-Dateien sind erlaubt.
        </Form.Text>
      </Form.Group>
    </div>
  );
};

export default MaintenanceReceiptUploader;
